// Global data stores
import { LABELS, POLYGONS, VERTICES } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import { updateSegmentLabel } from "./Labels";
import { removeSegmentDragControls } from "./DragControls";
import { clearUndoStack } from "../Global/UndoManager"; 
/**
 * Deletes a single polygon segment by index.
 * Cleans up its geometry, labels, obstacles, and panels from the scene.
 *
 * @param {number} index - Index of the polygon to delete.
 */
export function deleteSingleSegment(index) {
    const segment = POLYGONS[index];
    const scene = sceneManager.scene;

    removeShape(segment, index, scene);
    removeLabels(segment, scene);
    removeObstacles(segment, scene);
    removePanels(segment, scene);

    if (segment.setBacks.isSet) {
        removeSetbacks(segment, scene);
    }

    // Remove from global polygon array and reindex labels
    POLYGONS.splice(index, 1);
    updateSegmentLabel();
}

/**
 * Deletes all polygon segments.
 * Also clears VERTICES and LABELS.
 */
export function deleteAllSegments() {
    removeSegmentDragControls(); // Remove any active drag controls

    const scene = sceneManager.scene;

    // Remove each polygon and its components from the scene
    for (let i = 0; i < POLYGONS.length; i++) {
        const segment = POLYGONS[i];
        removeShape(segment, i, scene);
        removeLabels(segment, scene);
        removeObstacles(segment, scene);
        removePanels(segment, scene);
        if (segment.setBacks.isSet) {
            removeSetbacks(segment, scene);
        }
    }

    // Clear global stores
    POLYGONS.length = 0;
    VERTICES.length = 0;
    LABELS.measurements.length = 0;
    clearUndoStack();
}

/**
 * Removes the main polygon shape and its vertex mesh from the scene.
 *
 * @param {object} segment - The segment object containing shape and vertices.
 * @param {number} index - Index in VERTICES array to clean up.
 * @param {THREE.Scene} scene - The active Three.js scene.
 */
function removeShape(segment, index, scene) {
    const shape = segment.shape;

    // Remove and dispose all vertex children
    for (let child of shape.children) {
        if (child.name === "vertex") {
            scene.remove(child);
            child.geometry.dispose();
        }
    }

    // Remove polygon shape itself
    scene.remove(shape);
    shape.geometry.dispose();

    // Remove the associated vertex group from VERTICES
    VERTICES.splice(index, 1);
}

/**
 * Removes segment label and edge measurement labels from the scene.
 */
function removeLabels(segment, scene) {
    scene.remove(segment.label);

    for (let edge of segment.edges) {
        const label = LABELS.measurements[edge.id];
        scene.remove(label);
        delete LABELS.measurements[edge.id];
    }
}

/**
 * Removes all obstacle meshes from the scene and disposes of their resources.
 */
function removeObstacles(segment, scene) {
    for (let obs of segment.obstructions) {
        const shape = obs.mesh;
        scene.remove(shape);
        shape.geometry.dispose();
        shape.material.dispose();
    }
}

/**
 * Removes all panel meshes from the scene and clears the panel list.
 */
export function removePanels(segment, scene) {
    for (let panel of segment.panels) {
        const shape = panel.panel;
        scene.remove(shape);
        shape.geometry.dispose();
        shape.material.dispose();
    }
    segment.panels = [];
}

/**
 * Removes setback mesh from the scene if it exists.
 */
function removeSetbacks(segment, scene) {
    const setbacks = segment.setBacks.mesh;
    scene.remove(setbacks);
    setbacks.geometry.dispose();
    setbacks.material.dispose();
}
